import React, { useMemo, useState } from "react";
import {
  ShieldCheck, Copy, Check, AlertOctagon, AlertCircle, Sparkles,
  ArrowRight, Code,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";

// Public webhook signature verifier — pure client-side HMAC-SHA256.
// Customers who integrate QorTrace webhooks can drop in a raw delivery
// payload and shared secret and instantly tell whether the signature
// header is legit. No backend, nothing logged. Doubles as a debugging
// confidence-builder + dev-funnel landing page.

// --- helpers --------------------------------------------------------------

const enc = new TextEncoder();

async function hmacHex(secret, message) {
  const key = await crypto.subtle.importKey(
    "raw", enc.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false, ["sign"],
  );
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(message));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, "0")).join("");
}

function parseSignatureHeader(h) {
  // Accepts `t=<ts>,v1=<hex>` (Stripe-style) OR a raw hex digest.
  if (!h) return { ts: null, v1: "" };
  const parts = h.split(",").map((p) => p.trim());
  let ts = null, v1 = "";
  for (const p of parts) {
    if (p.startsWith("t=")) ts = p.slice(2);
    else if (p.startsWith("v1=")) v1 = p.slice(3);
  }
  if (!ts && !v1 && /^[a-f0-9]{32,}$/i.test(h.trim())) v1 = h.trim();
  return { ts, v1 };
}

function constantTimeEqual(a, b) {
  if (typeof a !== "string" || typeof b !== "string") return false;
  if (a.length !== b.length) return false;
  let r = 0;
  for (let i = 0; i < a.length; i++) r |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return r === 0;
}

// --- page -----------------------------------------------------------------

const SAMPLE = {
  body: '{"event":"webhook.test","id":"evt_demo","data":{"hello":"world"}}',
  secret: "whsec_demoSecretReplaceMeWithYourOwn1234",
  signature: "",  // computed below at runtime when "Use sample" clicked
};

export default function VerifyWebhook() {
  useDocumentTitle('Webhook Verifier — QorTrace');
  const [body, setBody] = useState("");
  const [secret, setSecret] = useState("");
  const [sig, setSig] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  const verify = async () => {
    setBusy(true);
    setResult(null);
    try {
      if (!body.trim() || !secret.trim() || !sig.trim()) {
        throw new Error("Body, secret, and signature header are all required.");
      }
      const { ts, v1 } = parseSignatureHeader(sig);
      if (!v1) throw new Error("Signature header missing the v1=<hex> part.");
      // QorTrace signs `{ts}.{body}` when ts is present, else just body.
      const message = ts ? `${ts}.${body}` : body;
      const expected = await hmacHex(secret.trim(), message);
      const match = constantTimeEqual(expected, v1.toLowerCase());
      let ageSeconds = null;
      if (ts && /^\d+$/.test(ts)) {
        ageSeconds = Math.floor(Date.now() / 1000) - parseInt(ts, 10);
      }
      setResult({ match, expected, given: v1, ts, ageSeconds });
    } catch (e) {
      setResult({ error: e.message || String(e) });
    } finally { setBusy(false); }
  };

  const useSample = async () => {
    const ts = Math.floor(Date.now() / 1000);
    const expected = await hmacHex(SAMPLE.secret, `${ts}.${SAMPLE.body}`);
    setBody(SAMPLE.body);
    setSecret(SAMPLE.secret);
    setSig(`t=${ts},v1=${expected}`);
    setResult(null);
  };

  const onCopy = async (key, value) => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
      else {
        const ta = document.createElement("textarea");
        ta.value = value; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? "" : k)), 1500);
    } catch (_) {}
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--qt-bg)" }}>
      <Header />
      <main className="flex-1">
        <section className="max-w-[900px] mx-auto px-6 sm:px-8 pt-16 pb-8">
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>QORTRACE · DEV TOOLS</div>
          <h1 className="qt-display text-4xl sm:text-5xl mt-2" style={{ color: "#fff" }}
              data-testid="verify-webhook-headline">
            Webhook signature verifier
          </h1>
          <p className="mt-4 max-w-2xl" style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
            Paste a raw webhook payload, the{" "}
            <code className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>X-QorTrace-Signature</code>{" "}
            header, and your shared secret. We compute the expected HMAC-SHA256
            in your browser and tell you whether they match — nothing leaves
            this page. Works for QorTrace + any other Stripe-style{" "}
            <code className="qt-mono text-xs" style={{ color: "var(--qt-cyan)" }}>t=…,v1=…</code> scheme.
          </p>
        </section>

        <section className="max-w-[900px] mx-auto px-6 sm:px-8 pb-16">
          <div className="qt-card" style={{ border: "1px solid var(--qt-border)" }}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} style={{ color: "var(--qt-cyan)" }} />
                <span className="qt-label" style={{ color: "var(--qt-cyan)" }}>
                  PURELY CLIENT-SIDE · WEB CRYPTO API
                </span>
              </div>
              <button type="button" onClick={useSample}
                      data-testid="verify-webhook-sample"
                      className="qt-btn qt-btn-secondary"
                      style={{ fontSize: 12, padding: "0.4rem 0.7rem" }}>
                <Sparkles size={11} /> Use sample
              </button>
            </div>

            <Field label="Raw request body (UTF-8)">
              <textarea data-testid="verify-webhook-body"
                        className="qt-input qt-mono text-xs"
                        rows={6}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder='{"event":"audit.delivered","id":"evt_…","data":{…}}' />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Signature header (X-QorTrace-Signature)">
                <input data-testid="verify-webhook-sig"
                       className="qt-input qt-mono text-xs"
                       value={sig}
                       onChange={(e) => setSig(e.target.value)}
                       placeholder="t=1707000000,v1=ab12…" />
              </Field>
              <Field label="Shared secret (whsec_…)">
                <input data-testid="verify-webhook-secret"
                       type="password"
                       className="qt-input qt-mono text-xs"
                       value={secret}
                       onChange={(e) => setSecret(e.target.value)}
                       placeholder="whsec_…" />
              </Field>
            </div>

            <div className="flex gap-2 mt-2 flex-wrap">
              <button type="button"
                      data-testid="verify-webhook-submit"
                      onClick={verify}
                      disabled={busy}
                      className="qt-btn qt-btn-primary"
                      style={{ fontSize: 13, padding: "0.6rem 1rem" }}>
                {busy ? "Computing…" : <>Verify <ArrowRight size={12} /></>}
              </button>
              <Link to="/account/webhooks"
                    data-testid="verify-webhook-manage"
                    className="qt-btn qt-btn-secondary"
                    style={{ fontSize: 13, padding: "0.6rem 1rem" }}>
                Manage webhooks <ArrowRight size={12} />
              </Link>
            </div>

            {result && <ResultBlock result={result} onCopy={onCopy} copied={copied} />}
          </div>

          {/* Reference snippets — JS + Python verifier code */}
          <ReferenceSnippets onCopy={onCopy} copied={copied} />
        </section>
      </main>
      <Footer />
    </div>
  );
}

function ResultBlock({ result, onCopy, copied }) {
  if (result.error) {
    return (
      <div className="mt-4 qt-card" data-testid="verify-webhook-result"
           style={{ background: "rgba(255,110,110,0.08)", border: "1px solid rgba(255,110,110,0.3)" }}>
        <div className="flex items-center gap-2">
          <AlertOctagon size={14} style={{ color: "#FF8E8E" }} />
          <span className="qt-label" style={{ color: "#FF8E8E" }}>INPUT ERROR</span>
        </div>
        <p className="text-sm mt-2" style={{ color: "var(--qt-text-2)" }}>{result.error}</p>
      </div>
    );
  }
  const ok = !!result.match;
  const stale = result.ageSeconds != null && result.ageSeconds > 300;
  return (
    <div className="mt-4 qt-card" data-testid="verify-webhook-result"
         style={{
           background: ok ? "rgba(0,210,106,0.05)" : "rgba(255,110,110,0.05)",
           border: `1px solid ${ok ? "rgba(0,210,106,0.4)" : "rgba(255,110,110,0.4)"}`,
         }}>
      <div className="flex items-center gap-2">
        {ok ? <Check size={14} style={{ color: "#1FDD83" }} />
            : <AlertCircle size={14} style={{ color: "#FF8E8E" }} />}
        <span className="qt-label" style={{ color: ok ? "#1FDD83" : "#FF8E8E" }}>
          {ok ? "SIGNATURE VALID" : "SIGNATURE MISMATCH"}
        </span>
      </div>
      {result.ts && (
        <p className="text-xs mt-2" style={{ color: stale ? "#FFB000" : "var(--qt-text-2)" }}>
          Timestamp: <code className="qt-mono">{result.ts}</code>
          {result.ageSeconds != null && (
            <> · age {result.ageSeconds}s{stale ? " (older than 5 min — consider replay protection)" : ""}</>
          )}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <SmallCode label="EXPECTED v1=" value={result.expected} onCopy={onCopy} copied={copied} ckey="expected" />
        <SmallCode label="GIVEN v1="    value={result.given}    onCopy={onCopy} copied={copied} ckey="given" />
      </div>
      {!ok && (
        <p className="text-xs mt-3" style={{ color: "var(--qt-text-2)", lineHeight: 1.55 }}>
          Most common causes: wrong shared secret, body was modified or
          re-encoded by a proxy, or the signature header includes only
          <code className="qt-mono"> v1=</code> without <code className="qt-mono">t=</code>{" "}
          (we still try the body-only digest as a fallback).
        </p>
      )}
    </div>
  );
}

function SmallCode({ label, value, onCopy, copied, ckey }) {
  return (
    <div>
      <div className="qt-mono text-[10px]" style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
        {label}
      </div>
      <div className="relative mt-1">
        <code data-testid={`verify-webhook-${ckey}`}
              className="qt-mono text-[11px] block"
              style={{
                padding: "0.5rem 2.5rem 0.5rem 0.6rem",
                background: "rgba(0,0,0,0.45)",
                borderRadius: 4, color: "#D6E0EE",
                wordBreak: "break-all",
              }}>{value}</code>
        <button type="button"
                onClick={() => onCopy(ckey, value)}
                data-testid={`verify-webhook-copy-${ckey}`}
                className="qt-btn qt-btn-secondary"
                style={{ position: "absolute", top: 4, right: 4,
                         fontSize: 11, padding: "0.2rem 0.45rem" }}>
          {copied === ckey ? <Check size={10} /> : <Copy size={10} />}
        </button>
      </div>
    </div>
  );
}

function ReferenceSnippets({ onCopy, copied }) {
  const [tab, setTab] = useState("js");
  const SNIPPETS = useMemo(() => ({
    js: `// Node.js — verify a QorTrace webhook signature
import { createHmac, timingSafeEqual } from "node:crypto";

import useDocumentTitle from "../hooks/useDocumentTitle";

export function verifyQorTrace(rawBody, sigHeader, secret) {
  const m = sigHeader.match(/t=(\\d+),v1=([a-f0-9]+)/);
  if (!m) return false;
  const [, ts, given] = m;
  const expected = createHmac("sha256", secret)
    .update(\`\${ts}.\${rawBody}\`).digest("hex");
  if (Math.abs(Date.now() / 1000 - Number(ts)) > 300) return false; // 5min
  return timingSafeEqual(Buffer.from(expected, "hex"),
                          Buffer.from(given, "hex"));
}`,
    py: `# Python — verify a QorTrace webhook signature
import hmac, hashlib, re, time

def verify_qortrace(raw_body: bytes, sig_header: str, secret: str) -> bool:
    m = re.match(r"t=(\\d+),v1=([a-f0-9]+)", sig_header or "")
    if not m: return False
    ts, given = m.group(1), m.group(2)
    if abs(time.time() - int(ts)) > 300: return False  # 5 min replay window
    expected = hmac.new(
        secret.encode(),
        f"{ts}.".encode() + raw_body,
        hashlib.sha256,
    ).hexdigest()
    return hmac.compare_digest(expected, given)`,
  }), []);
  const active = SNIPPETS[tab];
  return (
    <div className="mt-6 qt-card" data-testid="verify-webhook-snippets"
         style={{ border: "1px solid var(--qt-border)" }}>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Code size={14} style={{ color: "var(--qt-cyan)" }} />
        <span className="qt-label" style={{ color: "var(--qt-cyan)" }}>REFERENCE · SERVER-SIDE VERIFIERS</span>
        <div className="flex-1" />
        {Object.keys(SNIPPETS).map((id) => (
          <button key={id} type="button"
                  data-testid={`verify-webhook-snippet-tab-${id}`}
                  onClick={() => setTab(id)}
                  className="qt-mono text-[11px] px-3 py-1 rounded"
                  style={{
                    background: tab === id ? "var(--qt-cyan)" : "transparent",
                    color: tab === id ? "#0A1020" : "var(--qt-text-2)",
                    border: tab === id ? "1px solid var(--qt-cyan)" : "1px solid var(--qt-border)",
                    fontWeight: 700, letterSpacing: "0.10em",
                  }}>
            {id === "js" ? "NODE.JS" : "PYTHON"}
          </button>
        ))}
      </div>
      <div className="relative">
        <pre data-testid={`verify-webhook-snippet-${tab}`}
             className="qt-mono text-[11px]"
             style={{
               margin: 0, padding: "0.85rem 3rem 0.85rem 0.85rem",
               background: "rgba(0,0,0,0.45)",
               border: "1px solid var(--qt-border)",
               borderRadius: 6, color: "#D6E0EE",
               whiteSpace: "pre", overflow: "auto",
               lineHeight: 1.55, maxHeight: 320,
             }}>{active}</pre>
        <button type="button"
                onClick={() => onCopy(`snip-${tab}`, active)}
                data-testid={`verify-webhook-snippet-copy-${tab}`}
                className="qt-btn qt-btn-secondary"
                style={{ position: "absolute", top: 8, right: 8,
                         fontSize: 11, padding: "0.3rem 0.55rem" }}>
          {copied === `snip-${tab}`
            ? <><Check size={11} /> Copied</>
            : <><Copy size={11} /> Copy</>}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="qt-mono text-[10px] mb-1"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.12em" }}>
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}
